/**
 * 3D Model Tab
 *
 * The "3D Model" medium alongside Image, Video and Splat: image → 3D mesh
 * (GLB) via fal. Three selectable models, all image-to-3D:
 *   - Hunyuan3D 3.1 Pro (fal-ai/hunyuan-3d/v3.1/pro/image-to-3d)
 *   - TRELLIS 2           (fal-ai/trellis-2)
 *   - Meshy 7.1           (meshy/v7.1/image-to-3d)
 *
 * All endpoints are image-to-3D only (no text prompt input), so a reference
 * image is required to generate. Generation uses the async job queue (like
 * splats/videos): generateFalMesh submits to fal and returns a jobId
 * immediately; the client polls getGenerationJobStatus while the tab is open,
 * and the server saves the GLB as a first-class `mesh` asset when fal finishes
 * (surviving a closed tab, with an opt-in completion email). This is what fixed
 * the old synchronous-callable timeout on longer fal jobs.
 *
 * Named "3D Model" (not "Model") to avoid confusion with the AI Model selector
 * used elsewhere in the app.
 */

import FluxUI from './main.js';
import ImageUploadUtils from './image-upload-utils.js';
import useImageGenStore from './store.js';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@shared/services/firebase.js';
import posthog from 'posthog-js';
import { syncJobNotifyEmail } from './job-notify.js';
import {
  pollGenerationJob,
  forceJobNotifyEmail
} from '@shared/utils/generationJobs.js';
import { t } from './i18n/messages.js';
import { isTokenExhaustedError } from '@shared/utils/tokens.js';
import { MODEL3D_MODELS } from './model3d-models.js';
import { mountModelSelector } from './mount-model-selector.js';

const Model3DTab = {
  imageData: null,
  selectedModel: MODEL3D_MODELS[0].id,
  currentModelUrl: '',
  timerInterval: null,
  startTime: null,
  activePoll: null, // { promise, cancel } from pollGenerationJob
  activeJobId: null, // in-flight job — target of the email toggle

  // How long we keep polling before telling the user to check their gallery.
  // The job still completes server-side past this; we just stop watching
  // from this tab.
  POLL_MAX_MS: 20 * 60 * 1000,

  elements: {},

  init() {
    const container = document.getElementById('model3d-tab');
    if (!container) {
      console.error('3D Model Tab: container element not found');
      return;
    }

    this.createTabContent(container);
    this.getElements();
    this.setupEventListeners();
    this.updateTokenCost();
  },

  getModelConfig(id = this.selectedModel) {
    return MODEL3D_MODELS.find((m) => m.id === id) || MODEL3D_MODELS[0];
  },

  createTabContent(container) {
    container.innerHTML = `
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Parameters Column -->
        <div class="lg:col-span-1 bg-white rounded-lg shadow p-6">
          <h2 class="text-lg font-medium mb-1">${t('model3d.settingsHeading')}</h2>
          <p class="text-sm text-gray-500 mb-4">
            ${t('model3d.settingsDescription')}
          </p>

          <!-- Model Selection -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">${t('model3d.modelLabel')}</label>
            <div id="model3d-model-selector-container"></div>
          </div>

          <!-- Reference Image (required; these endpoints are image-to-3D) -->
          <div class="mb-4 param-group">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              ${t('model3d.referenceImageLabel')} <span class="text-red-500" title="${t('model3d.requiredTooltip')}">*</span>
            </label>
            <div class="flex flex-col space-y-2">
              <label id="model3d-image-upload-label" class="flex items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                <div class="flex flex-col items-center">
                  <p class="text-sm text-gray-500">${t('model3d.clickToUpload')}</p>
                  <p id="model3d-image-name" class="text-xs text-gray-400 mt-1">${t('model3d.noFileSelected')}</p>
                </div>
                <input id="model3d-image-input" type="file" class="hidden" accept="image/png, image/jpeg, image/jpg" />
              </label>
              <div id="model3d-image-preview-container" class="hidden relative">
                <img id="model3d-image-preview" class="w-full rounded-lg border border-gray-300" alt="${t('model3d.referenceImageAlt')}">
                <button id="model3d-image-clear" class="absolute top-2 right-2 p-1 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 hover:bg-red-50 shadow hover:shadow-lg transition-all duration-200" title="${t('model3d.clearImageTooltip')}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600 hover:text-red-600 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p class="text-xs text-gray-500">
                ${t('model3d.referenceImageHelp')}
              </p>
            </div>
          </div>

          <!-- Generate Button -->
          <button id="model3d-generate-btn" class="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-center gap-2">
            <svg id="model3d-generate-spinner" class="hidden animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span id="model3d-generate-text">${t('model3d.generateButton')}</span>
            <span class="inline-flex items-center rounded" style="background: rgba(0, 0, 0, 0.15); padding: 6px 8px; gap: 4px;">
              <img src="/ui_assets/token-image.png" alt="${t('model3d.tokenAlt')}" class="w-5 h-5" />
              <span id="model3d-token-cost" class="text-sm font-medium">${this.getModelConfig().tokenCost}</span>
            </span>
          </button>

          <!-- Email when done. Hidden until a job is in flight — mid-render
               it's the "you can close this tab" affordance; toggling writes
               through to the job doc (setGenerationJobNotify). Default on: a
               fal queue wait can stretch a job past what anyone keeps a tab
               open for. The email is suppressed server-side if the tab is
               still open when it finishes. -->
          <label id="model3d-notify-email-row" class="hidden flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer select-none">
            <input id="model3d-notify-email" type="checkbox" checked
              class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            ${t('model3d.notifyEmailLabel')}
          </label>
        </div>

        <!-- Preview Column -->
        <div class="lg:col-span-2 bg-white rounded-lg shadow">
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-lg font-medium">${t('model3d.previewHeading')}</h2>
          </div>
          <div class="p-6 flex flex-col items-center justify-center min-h-[500px]" id="model3d-preview-container">
            <div id="model3d-placeholder" class="text-center text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
              </svg>
              <p>${t('model3d.placeholderText')}</p>
            </div>
            <div id="model3d-loading" class="hidden flex flex-col items-center w-full max-w-md">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <div class="generator-rendering-content">
                <div class="generator-progress-container">
                  <div class="generator-progress-bar" id="model3d-progress-bar" style="width: 0%;"></div>
                  <div class="generator-progress-stripes"></div>
                </div>
                <span class="generator-progress-text" id="model3d-loading-text">${t('model3d.loadingText')}</span>
              </div>
            </div>
            <div id="model3d-result" class="hidden w-full">
              <iframe id="model3d-viewer-frame"
                class="w-full rounded-lg border border-gray-200 bg-gray-800"
                style="height: max(360px, 55vh);"
                title="${t('model3d.viewerFrameTitle')}"
                allow="fullscreen"></iframe>
              <p class="text-xs text-gray-500 mt-2 mb-3 text-center">
                ${t('model3d.resultHelp')}
              </p>
              <div class="flex items-center justify-center gap-3">
                <a id="model3d-open-btn" href="#" target="_blank" rel="noopener"
                  class="inline-flex items-center px-3 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium">
                  ${t('model3d.openInEditor')}
                </a>
                <button id="model3d-download-btn"
                  class="inline-flex items-center px-3 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">
                  ${t('model3d.downloadButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  },

  getElements() {
    this.elements = {
      modelSelectorContainer: document.getElementById(
        'model3d-model-selector-container'
      ),
      imageInput: document.getElementById('model3d-image-input'),
      imageName: document.getElementById('model3d-image-name'),
      imageUploadLabel: document.getElementById('model3d-image-upload-label'),
      imagePreviewContainer: document.getElementById(
        'model3d-image-preview-container'
      ),
      imagePreview: document.getElementById('model3d-image-preview'),
      imageClear: document.getElementById('model3d-image-clear'),
      generateBtn: document.getElementById('model3d-generate-btn'),
      generateSpinner: document.getElementById('model3d-generate-spinner'),
      generateText: document.getElementById('model3d-generate-text'),
      tokenCost: document.getElementById('model3d-token-cost'),
      placeholder: document.getElementById('model3d-placeholder'),
      loading: document.getElementById('model3d-loading'),
      loadingText: document.getElementById('model3d-loading-text'),
      progressBar: document.getElementById('model3d-progress-bar'),
      result: document.getElementById('model3d-result'),
      viewerFrame: document.getElementById('model3d-viewer-frame'),
      openBtn: document.getElementById('model3d-open-btn'),
      downloadBtn: document.getElementById('model3d-download-btn'),
      notifyEmail: document.getElementById('model3d-notify-email'),
      notifyEmailRow: document.getElementById('model3d-notify-email-row')
    };
  },

  setupEventListeners() {
    // Same dropdown as the image tab, with each model's token cost badged
    this.modelSelector = mountModelSelector(
      this.elements.modelSelectorContainer,
      {
        value: this.selectedModel,
        options: MODEL3D_MODELS,
        onChange: (modelId) => {
          this.selectedModel = modelId;
          this.modelSelector.update({ value: modelId });
          this.updateTokenCost();
        }
      }
    );

    this.elements.imageInput.addEventListener(
      'change',
      this.handleImageUpload.bind(this)
    );

    ImageUploadUtils.setupDragAndDrop(
      this.elements.imageUploadLabel,
      this.elements.imageInput,
      (dataUrl, fileName) => {
        this.setImage(dataUrl, fileName);
      }
    );

    this.elements.imageClear.addEventListener(
      'click',
      this.clearImage.bind(this)
    );

    this.elements.generateBtn.addEventListener(
      'click',
      this.handleGenerate.bind(this)
    );

    // Mid-render email opt-in writes through to the in-flight job doc
    this.elements.notifyEmail?.addEventListener('change', () => {
      syncJobNotifyEmail(this.activeJobId, this.elements.notifyEmail);
    });

    this.elements.downloadBtn.addEventListener(
      'click',
      this.downloadModel.bind(this)
    );
  },

  updateTokenCost() {
    this.elements.tokenCost.textContent = this.getModelConfig().tokenCost;
  },

  handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      this.setImage(event.target.result, file.name);
    };
    reader.readAsDataURL(file);
  },

  // Keep the reference image within what every model and the callable accept
  // (Hunyuan3D Pro takes up to 8MB; the callable request caps at ~10MB of
  // base64). Images already under the limits pass through untouched; larger
  // ones are scaled to 2048px. PNGs stay PNG so cutout transparency survives
  // (the models use it as the object mask); only if that's still too big do we
  // fall back to JPEG on white.
  async shrinkImage(dataUrl, maxDim = 2048, maxBytes = 4 * 1024 * 1024) {
    const bytesOf = (url) => (url.length - url.indexOf(',') - 1) * 0.75;
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });
    const longest = Math.max(img.width, img.height);
    if (longest <= maxDim && bytesOf(dataUrl) <= maxBytes) return dataUrl;

    const scale = Math.min(1, maxDim / longest);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (dataUrl.startsWith('data:image/png')) {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const png = canvas.toDataURL('image/png');
      if (bytesOf(png) <= maxBytes) return png;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    let out;
    for (const quality of [0.92, 0.85, 0.75]) {
      out = canvas.toDataURL('image/jpeg', quality);
      if (bytesOf(out) <= maxBytes) break;
    }
    return out;
  },

  async setImage(dataUrl, fileName) {
    try {
      dataUrl = await this.shrinkImage(dataUrl);
    } catch (err) {
      console.warn(
        'Could not downscale reference image; sending original',
        err
      );
    }
    this.imageData = dataUrl;
    this.elements.imageName.textContent = fileName;
    this.elements.imagePreview.src = dataUrl;
    this.elements.imageUploadLabel.classList.add('hidden');
    this.elements.imagePreviewContainer.classList.remove('hidden');
  },

  clearImage() {
    this.imageData = null;
    this.elements.imageInput.value = '';
    this.elements.imageName.textContent = t('model3d.noFileSelected');
    this.elements.imagePreview.src = '';
    this.elements.imagePreviewContainer.classList.add('hidden');
    this.elements.imageUploadLabel.classList.remove('hidden');
  },

  handleGenerate() {
    // A reference image is required; validate() surfaces a clear notice if
    // it's missing.
    this.startGeneration();
  },

  /**
   * Validate auth, tokens, and the reference image before calling the backend.
   */
  validate() {
    if (!window.authState || !window.authState.isAuthenticated) {
      useImageGenStore.getState().setModal('signin');
      return false;
    }

    const cost = this.getModelConfig().tokenCost;
    const tokens = window.authState.tokenProfile?.genToken || 0;
    if (tokens < cost) {
      window.dispatchEvent(
        new CustomEvent('openPurchaseModal', {
          detail: { tokenType: 'genToken' }
        })
      );
      return false;
    }

    // These endpoints are image-to-3D only.
    if (!this.imageData) {
      FluxUI.showNotification(t('model3d.addReferenceImageWarning'), 'warning');
      return false;
    }

    return true;
  },

  async startGeneration() {
    if (!this.validate()) return;

    this.stopPolling();
    this.toggleLoading(true);
    this.startTimer();

    const model = this.selectedModel;

    try {
      const generateFalMesh = httpsCallable(functions, 'generateFalMesh');

      const result = await generateFalMesh({
        model_id: model,
        input_image: this.imageData,
        scene_id: null,
        source: 'generator',
        // Opt-in completion email, recorded on the job doc. The server only
        // sends it if this tab isn't around to ack the result (i.e. closed).
        notify: { email: !!this.elements.notifyEmail?.checked }
      });

      if (!result.data || !result.data.success || !result.data.jobId) {
        throw new Error(t('model3d.couldNotStart'));
      }

      // The token was charged on submit; reflect that immediately. The job now
      // shows as a pending card in the gallery island (live Firestore listener),
      // so it persists across reloads/tabs without any client state here.
      window.dispatchEvent(new CustomEvent('tokenCountChanged'));

      posthog.capture('ai_render_used', {
        token_type: 'gen',
        model,
        source: 'generator',
        generation_type: 'mesh',
        is_pro_user: window.authState?.currentUser?.isPro || false
      });

      // The job is now really in flight — reveal the email opt-in (check it,
      // close the tab, get the result by email).
      this.activeJobId = result.data.jobId;
      this.elements.notifyEmailRow?.classList.remove('hidden');

      this.pollMeshStatus(result.data.jobId);
    } catch (error) {
      console.error('Error starting 3D generation:', error);
      this.stopTimer();
      this.failGeneration(this.errorMessage(error));
    }
  },

  // Drive the live UI off the shared getGenerationJobStatus poll until the
  // job is terminal. Any non-terminal status (queued|running|saving) just
  // keeps polling.
  pollMeshStatus(jobId) {
    this.stopPolling();
    this.activePoll = pollGenerationJob(jobId, {
      resultField: 'mesh_url',
      maxMs: this.POLL_MAX_MS
    });
    this.activePoll.promise
      .then((data) => {
        if (!data) return; // cancelled — a newer submit or teardown took over

        // Saved to the gallery server-side (works even if this tab had been
        // closed). Reflect it in the UI and refresh the gallery island.
        this.stopTimer();
        this.toggleLoading(false);
        this.showResult(data.mesh_url, data.assetId);
        window.dispatchEvent(new Event('assets:refresh'));
        FluxUI.showNotification(t('model3d.generatedSuccess'), 'success');
      })
      .catch(async (error) => {
        this.stopTimer();
        if (error.timedOut) {
          // The job outlived the poll window — unusual enough that the user
          // shouldn't have to babysit the tab: force the completion email on.
          const forced = await forceJobNotifyEmail(jobId);
          if (this.elements.notifyEmail) {
            this.elements.notifyEmail.checked = true;
          }
          this.failGeneration(
            forced
              ? t('model3d.timeoutForcedEmail')
              : t('model3d.timeoutCheckGallery'),
            forced ? 'warning' : 'error'
          );
          return;
        }
        // failed/canceled — the server refunds on failure; refresh the balance.
        window.dispatchEvent(new CustomEvent('tokenCountChanged'));
        this.failGeneration(
          error.jobError
            ? t('model3d.generationFailedWithReason', {
                jobError: error.jobError
              })
            : t('model3d.generationFailedRefunded')
        );
      });
  },

  stopPolling() {
    if (this.activePoll) {
      this.activePoll.cancel();
      this.activePoll = null;
    }
  },

  // Reset to the idle placeholder and surface an error toast. The gallery's
  // pending-job card clears itself when the job doc reaches a terminal state; a
  // local poll timeout just stops our polling — the job may still finish
  // server-side and surface in the gallery later.
  failGeneration(message, type = 'error') {
    this.stopPolling();
    this.toggleLoading(false);
    this.elements.placeholder.classList.remove('hidden');
    FluxUI.showNotification(message, type);
  },

  errorMessage(error) {
    if (error.code === 'unauthenticated') {
      return t('model3d.errorSignIn');
    }
    if (isTokenExhaustedError(error)) {
      return t('model3d.errorNoTokens');
    }
    if (error.message) {
      return t('model3d.errorFailedWithReason', { message: error.message });
    }
    return t('model3d.errorFailed');
  },

  showResult(meshUrl, assetId) {
    this.currentModelUrl = meshUrl || '';
    this.elements.placeholder.classList.add('hidden');
    this.elements.loading.classList.add('hidden');
    this.elements.result.classList.remove('hidden');

    // Live GLB preview in the shared mesh viewer (same page the gallery's
    // MeshDetailsModal embeds), pointed at the freshly generated model.
    this.elements.viewerFrame.src = `/model-viewer.html?src=${encodeURIComponent(
      this.currentModelUrl
    )}`;

    // "Open in 3DStreet" deep-links to the editor with the asset's detail modal
    // already open (#asset:OWNER/ID, same shape as the splat result). Falls
    // back to the raw GLB if we somehow lack an assetId/uid.
    const uid = auth.currentUser?.uid;
    this.elements.openBtn.href =
      assetId && uid
        ? `${window.location.origin}/?utm_source=generator&utm_medium=mesh_result&utm_campaign=open_in_editor#asset:${uid}/${assetId}`
        : this.currentModelUrl;
  },

  async downloadModel() {
    if (!this.currentModelUrl) return;
    try {
      const response = await fetch(this.currentModelUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${this.selectedModel}-${stamp}.glb`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Model download failed, opening in new tab:', e);
      window.open(this.currentModelUrl, '_blank');
    }
  },

  toggleLoading(isLoading) {
    if (isLoading) {
      this.elements.placeholder.classList.add('hidden');
      this.elements.result.classList.add('hidden');
      this.elements.loading.classList.remove('hidden');
      this.elements.generateBtn.disabled = true;
      this.elements.generateBtn.classList.add(
        'opacity-50',
        'cursor-not-allowed'
      );
      this.elements.generateSpinner.classList.remove('hidden');
      this.elements.generateText.textContent = t('model3d.generatingButton');
    } else {
      // Terminal (or reset): the email toggle only applies to an in-flight
      // job, so it leaves with the loading state.
      this.activeJobId = null;
      this.elements.notifyEmailRow?.classList.add('hidden');
      this.elements.loading.classList.add('hidden');
      this.elements.generateBtn.disabled = false;
      this.elements.generateBtn.classList.remove(
        'opacity-50',
        'cursor-not-allowed'
      );
      this.elements.generateSpinner.classList.add('hidden');
      this.elements.generateText.textContent = t('model3d.generateButton');
    }
  },

  startTimer() {
    this.startTime = Date.now();
    const estimated = this.getModelConfig().estimatedTime || 45;
    this.timerInterval = setInterval(() => {
      const elapsed = Math.round((Date.now() - this.startTime) / 1000);
      const progress = Math.min((elapsed / estimated) * 100, 100);
      if (this.elements.progressBar) {
        this.elements.progressBar.style.width = `${progress}%`;
      }
      this.elements.loadingText.textContent = t('model3d.timerProgress', {
        elapsed,
        estimated
      });
    }, 1000);
  },

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.elements.progressBar) {
      this.elements.progressBar.style.width = '0%';
    }
  }
};

export default Model3DTab;
